import { INVOICE_PAYMENT, type AppConfigRoot, type PaymentTargetInfo } from '@a1/shared';

import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid2 from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import Big from 'big.js';
import { invoicesV4FeatureFlagEnabled } from 'constants-ts/featureFlags';

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  onRefund: (invoiceId: number, externalRefNo: string, externalSource: string, refundAmount: string, createCreditNote: boolean) => void;
  paymentOption: string;
  externalRefNo: string;
  externalSource: string;
  setExternalRefNo: React.Dispatch<React.SetStateAction<string>>;
  setExternalSource: React.Dispatch<React.SetStateAction<string>>;
  targetInfo: PaymentTargetInfo | null;
  customTitle?: string;
  loading?: boolean;
}

const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onClose,
  onRefund,
  paymentOption,
  externalRefNo,
  externalSource,
  setExternalRefNo,
  setExternalSource,
  targetInfo,
  customTitle,
  loading
}: RefundDialogProps) => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  const isCreditNoteMandatory = !!appConfig?.integrations?.d365Finance?.enabled;
  const identifierToShow = targetInfo?.displayIdentifier || `ID: ${targetInfo?.id}`;
  const refundable = targetInfo?.refundable || 0;
  const defaultTitle = `Refund ${identifierToShow}`;
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [createCreditNote, setCreateCreditNote] = useState<boolean>(isCreditNoteMandatory);

  useEffect(() => {
    if (isCreditNoteMandatory) {
      setCreateCreditNote(true);
    }
  }, [isCreditNoteMandatory]);

  const isAmountValid = useMemo(() => {
    if (!refundAmount) {
      return false;
    }
    const refundAmountBig = Big(refundAmount);
    return refundAmountBig.gt(0) && refundAmountBig.lte(refundable);
  }, [refundAmount, refundable]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h4">{customTitle || defaultTitle}</Typography>
      </DialogTitle>
      <DialogContent>
        {paymentOption !== INVOICE_PAYMENT.ONLINE && (
          <>
            <Typography variant="h5">External Refund Details</Typography>
            <Grid2 size={12} mt={2}>
              <TextField
                id="externalTransactionRef"
                type="text"
                label="External Transaction ID/Reference"
                fullWidth
                value={externalRefNo}
                onChange={(e) => {
                  setExternalRefNo(e.target.value);
                }}
              />
            </Grid2>
            <Grid2 size={12} mt={2}>
              <TextField
                id="externalSource"
                type="text"
                label="External Payment Method/Channel"
                fullWidth
                value={externalSource}
                onChange={(e) => {
                  setExternalSource(e.target.value);
                }}
              />
            </Grid2>
          </>
        )}
        <Grid2 size={12} mt={2}>
          <TextField
            id="refundAmount"
            type="number"
            label={`Refund Amount (${targetInfo?.currency ?? ''})`}
            fullWidth
            inputProps={{ min: 0, max: refundable.toString(), step: '0.01', inputMode: 'decimal' }}
            value={refundAmount}
            onChange={(e) => {
              const val = e.target.value;
              // Only allow numbers and up to 2 decimals
              if (/^\d*(\.\d{0,2})?$/.test(val)) {
                setRefundAmount(val);
              } else if (/^\d*\.\d+/.test(val)) {
                // If more than 2 decimals, trim
                const [whole, dec] = val.split('.');
                setRefundAmount(`${whole}.${dec.slice(0, 2)}`);
              }
            }}
            helperText={`Refundable: ${refundable.toFixed(2)} ${targetInfo?.currency ?? ''}`}
            error={!!refundAmount && !isAmountValid}
          />
        </Grid2>

        {(invoicesV4FeatureFlagEnabled || isCreditNoteMandatory) && (
          <Grid2 size={12} mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCreditNoteMandatory || createCreditNote}
                  onChange={(e) => {
                    if (!isCreditNoteMandatory) {
                      setCreateCreditNote(e.target.checked);
                    }
                  }}
                  name="createCreditNote"
                  color="primary"
                  disabled={isCreditNoteMandatory}
                />
              }
              label={
                isCreditNoteMandatory
                  ? 'Create credit note on refund (required while D365 integration is enabled)'
                  : 'Create credit note on refund'
              }
            />
          </Grid2>
        )}

        <Grid2 size={12} mt={1}>
          {paymentOption === INVOICE_PAYMENT.ONLINE ? (
            <Alert severity="info">
              <b>Warning: This action is irreversible.</b> This will issue a monetary refund to the customer. While we process it instantly,
              it can take awhile for the funds to reach their bank.
            </Alert>
          ) : (
            <Alert severity="info">
              <b>Warning: This action is irreversible.</b> Please verify all details carefully and confirm the refund is justified before
              proceeding. Only complete this section for refund processed outside our system.
            </Alert>
          )}
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            setExternalRefNo('');
            setExternalSource('');
            setRefundAmount('');
            setCreateCreditNote(isCreditNoteMandatory);
          }}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          variant="contained"
          disabled={!isAmountValid}
          onClick={() => {
            if (!isAmountValid) {
              return;
            }
            onRefund(Number(targetInfo?.id), externalRefNo, externalSource, refundAmount, isCreditNoteMandatory || createCreditNote);
          }}
        >
          Refund
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundDialog;
